import { useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 20000;
const SOUND_URL = '/sounds/new-order.mp3';
const STORAGE_KEY = 'magere_admin_last_paid_check';

/**
 * Polls the backend for new paid orders and plays a cash-register sound
 * whenever new paid orders arrive since the last successful check.
 * Persists the "last check" cursor in localStorage so notifications don't repeat.
 */
export const useNewOrderSound = (storeId, enabled = true) => {
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const armedRef = useRef(false); // Requires a user gesture on iOS/Safari

  // Preload the audio element once
  useEffect(() => {
    if (!audioRef.current) {
      const audio = new Audio(SOUND_URL);
      audio.preload = 'auto';
      audio.volume = 0.9;
      audioRef.current = audio;
    }
    // Arm audio on first user interaction to satisfy browser autoplay policies
    const arm = () => {
      armedRef.current = true;
      const a = audioRef.current;
      if (a) {
        // Play + immediately pause to unlock audio context on iOS/Safari
        a.play().then(() => { a.pause(); a.currentTime = 0; }).catch(() => {});
      }
      window.removeEventListener('pointerdown', arm);
      window.removeEventListener('keydown', arm);
    };
    window.addEventListener('pointerdown', arm, { once: true });
    window.addEventListener('keydown', arm, { once: true });
    return () => {
      window.removeEventListener('pointerdown', arm);
      window.removeEventListener('keydown', arm);
    };
  }, []);

  const playSound = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    try {
      a.currentTime = 0;
      const p = a.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch { /* noop */ }
  }, []);

  const poll = useCallback(async () => {
    if (!storeId) return;
    const since = localStorage.getItem(STORAGE_KEY) || new Date().toISOString();
    try {
      const { data } = await axios.get(
        `${API_URL}/api/stores/${storeId}/orders/new-paid`,
        { params: { since }, withCredentials: true, timeout: 8000 }
      );
      if (data && data.count > 0) {
        playSound();
        const latest = data.latest || {};
        const total = latest.total ? `${latest.total.toFixed(2)} €` : '';
        toast.success(
          `Nouvelle commande payée${data.count > 1 ? `s (${data.count})` : ''} !`,
          {
            description: latest.order_number
              ? `Commande ${latest.order_number} — ${total}`
              : undefined,
            duration: 8000,
          }
        );
      }
      if (data && data.server_time) {
        localStorage.setItem(STORAGE_KEY, data.server_time);
      }
    } catch { /* silent - keep polling */ }
  }, [storeId, playSound]);

  useEffect(() => {
    if (!enabled || !storeId) return;
    // Initialize the cursor on first mount if missing
    if (!localStorage.getItem(STORAGE_KEY)) {
      localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    }
    poll();
    timerRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [enabled, storeId, poll]);

  return { playSound };
};

export default useNewOrderSound;
